import React, { useState, useEffect, useMemo } from 'react';
import { 
  GraduationCap, BookOpen, Satellite, Radio, Compass, 
  Activity, Cpu, FileText, Landmark, Award, Download, 
  Copy, Check, ArrowLeft, Sparkles, Upload, RefreshCw, 
  FileCheck, X, ShieldCheck, Crown
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';

interface PhdAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

const DEFAULT_PHD_DEMOS: Record<string, {
  field1LabelUk: string; field1ValueUk: string; field1ValuePl: string; field1ValueEn: string;
  field2LabelUk: string; field2ValueUk: string; field2ValuePl: string; field2ValueEn: string;
  field3LabelUk: string; field3ValueUk: string; field3ValuePl: string; field3ValueEn: string;
  field4LabelUk: string; field4ValueUk: string; field4ValuePl: string; field4ValueEn: string;
  field5LabelUk: string; field5ValueUk: string; field5ValuePl: string; field5ValueEn: string;
  field6LabelUk: string; field6ValueUk: string; field6ValuePl: string; field6ValueEn: string;
  notesTextUk: string; notesTextPl: string; notesTextEn: string;
}> = {
  'phd-eco-remote-sensing-analyst': {
    field1LabelUk: 'Цільовий регіон / Географічні координати (BBOX)',
    field1ValueUk: 'Басейн р. Сіверський Донець та Святі Гори (BBOX: 37.20, 48.90, 38.30, 49.35)',
    field1ValuePl: 'Dorzecze rzeki Doniec i Święte Góry (BBOX: 37.20, 48.90, 38.30, 49.35)',
    field1ValueEn: 'Siverskyi Donets Basin & Holy Mountains (BBOX: 37.20, 48.90, 38.30, 49.35)',
    field2LabelUk: 'Супутникові сузір\'я & Джерела даних',
    field2ValueUk: 'Sentinel-2 (L2A MSI 10m) + Sentinel-1 (C-band SAR IW) + PlanetScope (3m RGB-NIR)',
    field2ValuePl: 'Sentinel-2 (L2A MSI 10m) + Sentinel-1 (C-band SAR IW) + PlanetScope (3m RGB-NIR)',
    field2ValueEn: 'Sentinel-2 (L2A MSI 10m) + Sentinel-1 (C-band SAR IW) + PlanetScope (3m RGB-NIR)',
    field3LabelUk: 'Спектральні індекси & Алгоритми аналізу',
    field3ValueUk: 'NDVI (вегетація), NBR/dNBR (вигорання), MNDWI (забруднення вод), InSAR Coherence (вирви)',
    field3ValuePl: 'NDVI (wegetacja), NBR/dNBR (pożary), MNDWI (wody), InSAR Coherence (leje po wybuchach)',
    field3ValueEn: 'NDVI (vegetation), NBR/dNBR (burn severity), MNDWI (water), InSAR Coherence (craters)',
    field4LabelUk: 'Період спостереження (До / Під час / Після)',
    field4ValueUk: '2021-06-01 (базовий) ↔ 2023-08-15 ↔ 2026-06-01 (динаміка деградації)',
    field4ValuePl: '2021-06-01 (bazowy) ↔ 2023-08-15 ↔ 2026-06-01 (dynamika degradacji)',
    field4ValueEn: '2021-06-01 (baseline) ↔ 2023-08-15 ↔ 2026-06-01 (degradation dynamics)',
    field5LabelUk: 'Оцінювані екологічні наслідки (Екоцид)',
    field5ValueUk: 'Знищення 42 000 га соснових лісів, хімічне забруднення річки, воронки артилерії',
    field5ValuePl: 'Zniszczenie 42 000 ha lasów sosnowych, skażenie chemiczne rzeki, leje artyleryjskie',
    field5ValueEn: 'Destruction of 42,000 ha pine forests, river chemical pollution, artillery crater density',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 2 (Аналіз стану України) та Розділ 3 (Моделі мультиспектрального ДЗЗ)',
    field6ValuePl: 'Rozdział 2 (Stan Ukrainy) oraz Rozdział 3 (Modele teledetekcji multispektralnej)',
    field6ValueEn: 'Chapter 2 (State of Ukraine) & Chapter 3 (Multispectral EO processing models)',
    notesTextUk: 'Здійснити комплексний науково-методологічний аналіз супутникових даних ДЗЗ для оцінки екологічних наслідків воєнних дій у басейні р. Сіверський Донець. Розрахувати площі вигорання за індексами dNBR, динаміку деградації ґрунтів за InSAR Coherence, побудувати картосхему екологічного ураження та сформувати структурований науковий підрозділ дисертації у Word (.docx) за ДСТУ 4163:2020.',
    notesTextPl: 'Przeprowadzić kompleksową analizę teledetekcyjną danych satelitarnych w celu oceny szkód wojennych w dorzeczu Dońca. Obliczyć obszary pożarów według wskaźnika dNBR, dynamikę degradacji gleb i wygenerować podrozdział rozprawy doktorskiej w formacie Word (.docx).',
    notesTextEn: 'Execute a comprehensive scientific remote sensing analysis using multi-spectral and SAR satellite data to evaluate military ecocide in the Siverskyi Donets basin. Calculate burn severity maps via dNBR, ground displacement via InSAR, and format a structured doctoral dissertation section in Word (.docx).'
  },
  'phd-skako-space-situational-guard': {
    field1LabelUk: 'Орбітальне угруповання / Супутники ДЗЗ',
    field1ValueUk: 'Національний мікросупутник «Січ-2-30» + сузір\'я Sentinel-1/2, ICEYE (LEO, 450–700 км)',
    field1ValuePl: 'Satelita narodowy «Sicz-2-30» + konstelacja Sentinel-1/2, ICEYE (LEO, 450–700 km)',
    field1ValueEn: 'National satellite Sich-2-30 + Sentinel-1/2, ICEYE constellations (LEO, 450–700 km)',
    field2LabelUk: 'Параметри СКАКО & Бази космічного сміття',
    field2ValueUk: 'Каталог NORAD TLE, ESA DISCOS, EU SST Database, станції радіоконтролю НЦУВКЗ',
    field2ValuePl: 'Katalog NORAD TLE, ESA DISCOS, Baza EU SST, stacje radiokontroli NCUVKZ',
    field2ValueEn: 'NORAD TLE Catalog, ESA DISCOS, EU SST Database, NCUVKZ radio monitoring stations',
    field3LabelUk: 'Критерії оцінки ризиків (Conjunction Assessment)',
    field3ValueUk: 'Мінімальна дистанція зближення d_miss < 1000 м, ймовірність зіткнення Pc > 1e-4',
    field3ValuePl: 'Minimalny dystans zbliżenia d_miss < 1000 m, prawdopodobieństwo kolizji Pc > 1e-4',
    field3ValueEn: 'Miss distance threshold d_miss < 1000 m, collision probability Pc > 1e-4',
    field4LabelUk: 'Радіочастотний моніторинг завад (RFI)',
    field4ValueUk: 'X-band (8.025–8.400 GHz) низхідні лінії прийому знімків ДЗЗ станцій Дунаївці / Золочів',
    field4ValuePl: 'Pasmo X (8.025–8.400 GHz) łącza odbioru danych teledetekcji stacji Dunajowce / Złoczów',
    field4ValueEn: 'X-band (8.025–8.400 GHz) Earth observation telemetry downlink at Dunaivtsi / Zolochiv',
    field5LabelUk: 'Заходи безпеки та маневри ухилення (CAM)',
    field5ValueUk: 'Collision Avoidance Maneuver (CAM) розрахунок дельта-V та захист ліній шифруванням',
    field5ValuePl: 'Collision Avoidance Maneuver (CAM) obliczenie delta-V i szyfrowanie transmisji',
    field5ValueEn: 'Collision Avoidance Maneuver (CAM) delta-V budget & encrypted telemetry hardening',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 2 (Інфраструктура України) та Розділ 4 (Архітектура захисту СКАКО)',
    field6ValuePl: 'Rozdział 2 (Infrastruktura) oraz Rozdział 4 (Architektura ochrony SKAKO/SSA)',
    field6ValueEn: 'Chapter 2 (Capabilities) & Chapter 4 (Space Situational Awareness Architecture)',
    notesTextUk: 'Розробити математичні та організаційні моделі підсистеми СКАКО для забезпечення безпеки космічних апаратів екологічного моніторингу України. Сформувати алгоритми оцінки ймовірності зіткнень із космічним сміттям, захисту радіоканалів прийому ДЗЗ від постановки завад (RFI) та підготувати підрозділ дисертації у Word (.docx).',
    notesTextPl: 'Opracować modele matematyczne i organizacyjne podsystemu SKAKO/SSA dla ochrony satelitów ekologicznych Ukrainy. Zdefiniować algorytmy unikania kolizji ze śmieciami kosmicznymi oraz zabezpieczenia stacji odbiorczych.',
    notesTextEn: 'Formulate mathematical and operational SSA/SKAKO models to protect Earth observation satellites monitoring Ukraine\'s environment. Establish collision probability estimation algorithms, RFI mitigation on X-band downlinks, and generate a doctoral dissertation chapter in Word (.docx).'
  },
  'phd-gnss-geodynamics-surveyor': {
    field1LabelUk: 'Мережі перманентних ГНСС-станцій України',
    field1ValueUk: 'Мережа НЦУВКЗ (24 станції) + GeoTerrace НУ «ЛП» + System.NET (понад 120 станцій)',
    field1ValuePl: 'Sieć NCUVKZ (24 stacje) + GeoTerrace Politechniki Lwowskiej + System.NET (120+ stacji)',
    field1ValueEn: 'NCUVKZ Network (24 stations) + GeoTerrace Lviv Polytech + System.NET (120+ stations)',
    field2LabelUk: 'Об\'єкти геодинамічного & екологічного моніторингу',
    field2ValueUk: 'Дамба ДніпроГЕС, зона Каховського водосховища, карстові провали Солотвино та Стебник',
    field2ValuePl: 'Zapora DnieproGES, zbiornik kachowski, zapadliska krasowe Sołotwina i Stebnik',
    field2ValueEn: 'DniproHES Dam, Kakhovka reservoir basin, Solotvyno & Stebnyk karst sinkholes',
    field3LabelUk: 'Методи обробки ГНСС-вимірів',
    field3ValueUk: 'Precise Point Positioning (PPP) + диференційний подвійний різницевий фазовий аналіз (DD)',
    field3ValuePl: 'Precise Point Positioning (PPP) + analiza różnicowa podwójnych różnic fazowych (DD)',
    field3ValueEn: 'Precise Point Positioning (PPP) + Double-Difference carrier-phase network analysis (DD)',
    field4LabelUk: 'Точність детекції просторових зміщень',
    field4ValueUk: 'Планова точність: 1.2–2.0 мм, висотна: 2.5–4.0 мм (добові ряди за 2021–2026 рр.)',
    field4ValuePl: 'Dokładność pozioma: 1.2–2.0 mm, pionowa: 2.5–4.0 mm (szeregi dobowe 2021–2026)',
    field4ValueEn: 'Horizontal precision: 1.2–2.0 mm, vertical: 2.5–4.0 mm (daily time-series 2021–2026)',
    field5LabelUk: 'ГНСС-метеорологія (Тропосферні параметри)',
    field5ValueUk: 'Zenith Total Delay (ZTD) та Precipitable Water Vapor (PWV) для аналізу злив і паводків',
    field5ValuePl: 'Zenith Total Delay (ZTD) oraz Precipitable Water Vapor (PWV) do prognoz powodziowych',
    field5ValueEn: 'Zenith Total Delay (ZTD) & Precipitable Water Vapor (PWV) for flood risk analytics',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 2 (Аналіз спроможностей) та Розділ 3 (Математичні моделі геодезичного моніторингу)',
    field6ValuePl: 'Rozdział 2 (Potencjał geodezyjny) oraz Rozdział 3 (Modele geodynamiczne)',
    field6ValueEn: 'Chapter 2 (Geodetic Infrastructure) & Chapter 3 (Geodynamic & Tropospheric Models)',
    notesTextUk: 'Сформувати науковий розділ дисертації щодо інтеграції перманентних мереж ГНСС у загальну систему екологічної безпеки України. Дослідити часові ряди зміщень дамби ДніпроГЕС та карстових зон Солотвино, розрахувати кореляцію між тропосферними затримками ZTD та аномальними опадами і експортувати розділ у Word (.docx).',
    notesTextPl: 'Opracować rozdział dysertacji dotyczący integracji sieci permanentnych GNSS z systemem bezpieczeństwa ekologicznego. Przeanalizować mikrodeformacje zapory DnieproGES i strefy krasowej Sołotwina oraz wyeksportować raport do Word (.docx).',
    notesTextEn: 'Synthesize a doctoral dissertation chapter on integrating GNSS permanent networks into Ukraine\'s ecological safety architecture. Process displacement time-series for DniproHES dam and Solotvyno karst sinkholes, calculate ZTD tropospheric correlations, and export to Word (.docx).'
  },
  'phd-special-control-geophysicist': {
    field1LabelUk: 'Мережа станцій Спецконтролю (ГЦСК ДКАУ)',
    field1ValueUk: 'Сейсмічні станції PS-45 (Малин), Кам\'янець-Подільський, Ужгород, Полтава + інфразвук',
    field1ValuePl: 'Stacje sejsmiczne PS-45 (Malin), Kamieniec Podolski, Użhorod, Połtawa + infradźwięki',
    field1ValueEn: 'Seismic stations PS-45 (Malyn), Kamianets-Podilskyi, Uzhhorod, Poltava + infrasound arrays',
    field2LabelUk: 'Фізичні канали геофізичного спостереження',
    field2ValueUk: 'Сейсмоакустика (0.5–20 Гц), інфразвук (0.01–10 Гц), геомагнітне поле, гамма-спектрометрія',
    field2ValuePl: 'Sejismoakustyka (0.5–20 Hz), infradźwięki (0.01–10 Hz), pole geomagnetyczne, promieniowanie',
    field2ValueEn: 'Seismo-acoustics (0.5–20 Hz), infrasound (0.01–10 Hz), geomagnetic variations, radionuclides',
    field3LabelUk: 'Досліджувані техногенні та природні аномалії',
    field3ValueUk: 'Затоплення шахт Донбасу (гідродинамічні удари), вибухові навантаження, стан ЗАЕС/ЧАЕС',
    field3ValuePl: 'Zatapianie kopalń Donbasu (wstrząsy górotworu), wybuchy wojenne, strefy ZAwES/Czarnobyl',
    field3ValueEn: 'Donbas mine flooding (hydrodynamic rock bursts), warfare detonations, ZNPP/ChNPP security',
    field4LabelUk: 'Методи спектрального та часового аналізу',
    field4ValueUk: 'Частотно-часовий вейвлет-аналіз (CWT), метод поляризаційної фільтрації та крос-кореляція',
    field4ValuePl: 'Analiza falkowa (CWT), filtracja polaryzacyjna oraz korelacja wzajemna sygnałów',
    field4ValueEn: 'Continuous Wavelet Transform (CWT), polarization filtering, and cross-correlation arrays',
    field5LabelUk: 'Критерії достовірності ідентифікації подій',
    field5ValueUk: 'Ймовірність правильної класифікації P > 0.94 за каталогами подій ГЦСК ДКАУ',
    field5ValuePl: 'Prawdopodobieństwo poprawnej klasyfikacji P > 0.94 według katalogów GCKS',
    field5ValueEn: 'Event classification probability P > 0.94 benchmarked against GCKS historical catalogs',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 2 (Спеціальний контроль) та Розділ 3 (Комплексування геофізичних сигналів)',
    field6ValuePl: 'Rozdział 2 (Kontrola Specjalna) oraz Rozdział 3 (Integracja sygnałów geofizycznych)',
    field6ValueEn: 'Chapter 2 (Special Control Network) & Chapter 3 (Geophysical Signal Fusion)',
    notesTextUk: 'Скласти науковий підрозділ дисертації щодо ролі системи спеціального контролю ГЦСК у виявленні підземних, приземних та радіаційних загроз довкіллю України. Провести аналіз сейсмоакустичних сигналів затоплення вугільних шахт Донбасу, побудувати моделі розповсюдження інфразвуку від потужних вибухів та оформити результати у Word (.docx).',
    notesTextPl: 'Przygotować podrozdział rozprawy doktorskiej dotyczący roli Głównego Centrum Kontroli Specjalnej (GCKS) w wykrywaniu zagrożeń sejsmicznych i radiacyjnych w Ukrainie. Sformatować raport w Word (.docx).',
    notesTextEn: 'Prepare a doctoral dissertation section analyzing the role of GCKS special control stations in detecting subterranean, surface, and radiation threats in Ukraine. Process seismo-acoustic signals of flooded Donbas coal mines, model infrasound propagation from high-energy explosions, and export to Word (.docx).'
  },
  'phd-data-fusion-math-modeler': {
    field1LabelUk: 'Гетерогенні джерела даних для комплексування',
    field1ValueUk: 'Мультиспектральне ДЗЗ (Sentinel-2) + SAR InSAR (Sentinel-1) + ГНСС (Зсуви) + ГЦСК (Сейсміка)',
    field1ValuePl: 'Teledetekcja (Sentinel-2) + SAR InSAR (Sentinel-1) + GNSS (Przesunięcia) + GCKS (Sejsmika)',
    field1ValueEn: 'Multispectral EO (Sentinel-2) + SAR InSAR (Sentinel-1) + GNSS (Displacements) + GCKS (Seismic)',
    field2LabelUk: 'Математичний апарат (Наукова новизна)',
    field2ValueUk: 'Ієрархічний фільтр Кальмана (Unscented Kalman Filter) + Динамічні Байєсівські мережі',
    field2ValuePl: 'Filtr Kalmana (Unscented Kalman Filter) + Dynamiczne Sieci Bayesowskie',
    field2ValueEn: 'Unscented Kalman Filter (UKF) + Dynamic Bayesian Networks + Evidence Theory (Dempster-Shafer)',
    field3LabelUk: 'Просторово-часова дискретизація моделі',
    field3ValueUk: 'Просторова сітка 10x10 м, часовий крок дискретизації: 1 година (ГНСС/ГЦСК) / 5 днів (ДЗЗ)',
    field3ValuePl: 'Siatka przestrzenna 10x10 m, krok czasowy: 1 godzina (GNSS/GCKS) / 5 dni (EO)',
    field3ValueEn: 'Spatial grid 10x10 m, temporal step: 1 hour (GNSS/GCKS) / 5 days (Satellite EO)',
    field4LabelUk: 'Інтегральний показник екологічного ризику (IEHI)',
    field4ValueUk: 'Integrated Ecological Hazard Index: IEHI = f(dNBR, InSAR_deform, GNSS_disp, Seismic_energy)',
    field4ValuePl: 'Integrated Ecological Hazard Index: IEHI = f(dNBR, InSAR_deform, GNSS_disp, Seismic_energy)',
    field4ValueEn: 'Integrated Ecological Hazard Index: IEHI = f(dNBR, InSAR_deform, GNSS_disp, Seismic_energy)',
    field5LabelUk: 'Критерії верифікації та статистична значущість',
    field5ValueUk: 'ROC-AUC > 0.92, Root Mean Square Error (RMSE) < 0.08, статистичний рівень значущості p < 0.001',
    field5ValuePl: 'ROC-AUC > 0.92, Błąd RMSE < 0.08, poziom istotności statystycznej p < 0.001',
    field5ValueEn: 'ROC-AUC > 0.92, Root Mean Square Error (RMSE) < 0.08, significance level p < 0.001',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 3: Методологічні основи та моделі комплексної обробки гетерогенних даних',
    field6ValuePl: 'Rozdział 3: Podstawy metodologiczne i modele matematyczne fuzji danych heterogenicznych',
    field6ValueEn: 'Chapter 3: Methodological Framework and Mathematical Models for Heterogeneous Data Fusion',
    notesTextUk: 'Сформувати повний текст Розділу 3 дисертації доктора філософії (Наукова новизна). Розробити теоретичні основи та математичні формули алгоритму Data Fusion для комплексування розріджених даних супутникового ДЗЗ, СКАКО, мереж ГНСС та геофізичного спецконтролю ГЦСК. Навести блок-схеми алгоритмів, матриці коваріації фільтра Кальмана та експортувати у Word (.docx) за ДСТУ 4163:2020.',
    notesTextPl: 'Opracować pełny tekst Rozdziału 3 rozprawy doktorskiej (Nowość Naukowa). Sformułować modele fuzji danych satelitarnych, GNSS i geofizycznych przy użyciu filtrów Kalmana i sieci bayesowskich. Wyeksportować do Word (.docx).',
    notesTextEn: 'Generate the complete Chapter 3 of the PhD dissertation representing the core Scientific Novelty. Formulate the mathematical equations of the multi-sensor Data Fusion framework integrating Satellite EO, SSA/SKAKO, GNSS geodesy, and GCKS geophysics. Include covariance matrices, LaTeX equations, and export to Word (.docx).'
  },
  'phd-dissertation-lead-orchestrator': {
    field1LabelUk: 'Тема дисертації & Наукова спеціальність',
    field1ValueUk: '«Космічна підтримка екологічного сектору України» / 103 Науки про Землю (або 122/126/183)',
    field1ValuePl: '«Wsparcie kosmiczne sektora ekologicznego Ukrainy» / Nauki o Ziemi i Środowisku',
    field1ValueEn: '«Space Support for the Environmental Sector of Ukraine» / Earth Sciences & Remote Sensing',
    field2LabelUk: 'Об\'єкт & Предмет наукового дослідження',
    field2ValueUk: 'Об\'єкт: Процеси екологічного моніторингу / Предмет: Методи та моделі комплексування ДЗЗ, СКАКО, ГНСС і спецконтролю',
    field2ValuePl: 'Obiekt: Procesy monitoringu środowiska / Przedmiot: Metody i modele fuzji teledetekcji, SSA, GNSS i geofizyki',
    field2ValueEn: 'Object: Environmental monitoring processes / Subject: Models & methods of EO, SSA, GNSS, and special control data fusion',
    field3LabelUk: 'Мета дослідження & 5 наукових завдань',
    field3ValueUk: 'Мета: Підвищення достовірності та оперативності екологічного моніторингу України шляхом створення інтегрованої системи',
    field3ValuePl: 'Cel: Zwiększenie wiarygodności i operacyjności monitoringu środowiska Ukrainy poprzez system zintegrowany',
    field3ValueEn: 'Aim: Enhancing reliability and operational speed of environmental monitoring in Ukraine via unified space-geophysical architecture',
    field4LabelUk: 'Положення наукової новизни (3–4 тези)',
    field4ValueUk: '1) Модель Data Fusion 4 доменів; 2) Метод InSAR-ГНСС детекції екоциду; 3) Архітектура НІС-ЕКО з адаптивним фільтром',
    field4ValuePl: '1) Model fuzji 4 domen danych; 2) Metoda InSAR-GNSS detekcji ekobójstwa; 3) Architektura systemu NIS-EKO',
    field4ValueEn: '1) 4-domain Data Fusion mathematical model; 2) Combined InSAR-GNSS ecocide detection method; 3) NIS-ECO architecture',
    field5LabelUk: 'Практичне значення для держави & Апробація',
    field5ValueUk: 'Впровадження у Міндовкілля, ДКАУ, ДСНС, Генеральному штабі ЗСУ + 4 акти впровадження',
    field5ValuePl: 'Wdrożenie w Ministerstwie Środowiska, Agencji Kosmicznej DKAU, Państwowej Służbie Ratowniczej',
    field5ValueEn: 'Implementation in Ministry of Environment, State Space Agency, SES of Ukraine, Armed Forces + 4 adoption acts',
    field6LabelUk: 'Зв\'язок з науковими програмами України',
    field6ValueUk: 'Загальнодержавна цільова науково-технічна космічна програма України, програми НАН України',
    field6ValuePl: 'Państwowy program kosmiczny Ukrainy, programy badawcze Narodowej Akademii Nauk Ukrainy',
    field6ValueEn: 'National Targeted Scientific & Technical Space Program of Ukraine, NAS of Ukraine state research programs',
    notesTextUk: 'Здійснити повне академічне формування Вступу дисертації доктора філософії (PhD) за вимогами МОН України та НАЗЯВО (ПКМУ № 44). Чітко викласти актуальність, мету, 5 завдань, об\'єкт, предмет, методи, 4 положення наукової новизни, практичне значення, зв\'язок з держпрограмами та апробацію у Word (.docx) за ДСТУ 4163:2020.',
    notesTextPl: 'Wygenerować kompletny Wstęp rozprawy doktorskiej (PhD) zgodnie z normami Ministerstwa Nauki i NAQA. Przedstawić cel, zadania, nowość naukową, znaczenie praktyczne i wdrożenia w formacie Word (.docx).',
    notesTextEn: 'Generate the complete Introduction and Academic Passport of the PhD dissertation in strict compliance with doctoral accreditation standards (Order No. 44). Detail topic relevance, 5 research objectives, object & subject, scientific novelty claims, and practical impact in Word (.docx).'
  },
  'phd-global-trends-comparator': {
    field1LabelUk: 'Міжнародні космічні програми для аналізу',
    field1ValueUk: 'EU Copernicus (Sentinel-1..6), NASA Earth System Observatory, Destination Earth (DestinE), ESA Living Planet',
    field1ValuePl: 'EU Copernicus (Sentinel-1..6), NASA Earth System Observatory, Destination Earth (DestinE), ESA Living Planet',
    field1ValueEn: 'EU Copernicus (Sentinel-1..6), NASA Earth System Observatory, Destination Earth (DestinE), ESA Living Planet',
    field2LabelUk: 'Системи космічної безпеки та SSA/SST',
    field2ValueUk: 'EU SST (Space Surveillance & Tracking), US Space Command SSA, ESA Space Safety Programme (SWE/NEO)',
    field2ValuePl: 'EU SST (Space Surveillance & Tracking), US Space Command SSA, ESA Space Safety Programme (SWE/NEO)',
    field2ValueEn: 'EU SST (Space Surveillance & Tracking), US Space Command SSA, ESA Space Safety Programme (SWE/NEO)',
    field3LabelUk: 'Глобальні стандарти обміну даними ДЗЗ',
    field3ValueUk: 'OGC WMS/WFS/WMTS, STAC (SpatioTemporal Asset Catalog), CEOS WGISS, INSPIRE Directive (2007/2/EC)',
    field3ValuePl: 'OGC WMS/WFS/WMTS, STAC (SpatioTemporal Asset Catalog), CEOS WGISS, Dyrektywa INSPIRE (2007/2/EC)',
    field3ValueEn: 'OGC WMS/WFS/WMTS, STAC (SpatioTemporal Asset Catalog), CEOS WGISS, INSPIRE Directive (2007/2/EC)',
    field4LabelUk: 'Порівняльні критерії ефективності систем',
    field4ValueUk: 'Час поновлення даних (Revisit time), просторова розрізненість (GSD), рівень автоматизації ШІ (AI Data-Pipelines)',
    field4ValuePl: 'Czas rewizyty (Revisit time), rozdzielczość terenowa (GSD), poziom automatyzacji AI',
    field4ValueEn: 'Revisit frequency, Ground Sampling Distance (GSD), AI data pipeline processing latency',
    field5LabelUk: 'Виявлені прогалини світового досвіду щодо зон війни',
    field5ValueUk: 'Відсутність стандартизованих протоколів оперативного моніторингу екоциду в умовах активних бойових дій',
    field5ValuePl: 'Brak znormalizowanych protokołów monitoringu ekobójstwa w warunkach aktywnych działań wojennych',
    field5ValueEn: 'Lack of standardized operational protocols for ecocide and war environmental damage verification under active combat',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 1: Світовий досвід та тенденції космічної підтримки екологічного сектору',
    field6ValuePl: 'Rozdział 1: Doświadczenia światowe i trendy wsparcia kosmicznego dla ekologii',
    field6ValueEn: 'Chapter 1: Global State-of-the-Art and International Space-Environmental Systems Survey',
    notesTextUk: 'Сформувати повний текст Розділу 1 дисертації (Світові тенденції та огляд літератури). Провести глибокий порівняльний аналіз програм Copernicus, NASA ESO, Destination Earth, систем SSA/SST та протоколів CEOS/INSPIRE. Виявити наукові прогалини щодо фіксації екологічних воєнних наслідків та оформити розділ (30–35 стор.) у Word (.docx).',
    notesTextPl: 'Opracować pełny tekst Rozdziału 1 rozprawy doktorskiej (Trendy światowe i przegląd literatury). Przeprowadzić analizę porównawczą programów Copernicus, NASA ESO i DestinE w formacie Word (.docx).',
    notesTextEn: 'Synthesize the complete Chapter 1 of the PhD dissertation (Literature Review and Global State-of-the-Art). Benchmarking Copernicus, NASA ESO, Destination Earth digital twins, and ESA SSA programs with 60+ Scopus citations in Word (.docx).'
  },
  'phd-ukraine-capabilities-auditor': {
    field1LabelUk: 'Національні космічні та наукові інституції',
    field1ValueUk: 'ДКАУ, НЦУВКЗ (Дунаївці, Золочів), ГЦСК, Інститут космічних досліджень НАНУ-ДКАУ, Міндовкілля',
    field1ValuePl: 'DKAU, NCUVKZ (Dunajowce, Złoczów), GCKS, Instytut Badań Kosmicznych NAN Ukrainy, Ministerstwo Środowiska',
    field1ValueEn: 'SSAU, NCUVKZ (Dunaivtsi, Zolochiv), GCKS, Space Research Institute NASU-SSAU, Ministry of Environment',
    field2LabelUk: 'Нормативно-правова база України',
    field2ValueUk: 'Закон України «Про космічну діяльність», Закон «Про охорону навколишнього середовища», ПКМУ № 55',
    field2ValuePl: 'Ustawa Ukrainy «O działalności kosmicznej», Ustawa «O ochronie środowiska», Rozporządzenia Rządu',
    field2ValueEn: 'Law of Ukraine «On Space Activity», Law «On Environmental Protection», Cabinet Decrees',
    field3LabelUk: 'Оцінка наслідків збройної агресії (Екоцид)',
    field3ValueUk: 'Пошкоджено понад 3 млн га лісів, знищено Каховську ГЕС, забруднення ґрунтів важкими металами',
    field3ValuePl: 'Uszkodzono ponad 3 mln ha lasów, zniszczono zaporę Kachowską, skażenie gleb metalami ciężkimi',
    field3ValueEn: 'Over 3M ha forests damaged, destruction of Kakhovka HPP dam, heavy metal toxic soil contamination',
    field4LabelUk: 'SWOT-аналіз космічно-екологічного сектору',
    field4ValueUk: 'Strengths: потужна наземна база ГЦСК/НЦУВКЗ; Weaknesses: відсутність власних активних супутників на орбіті',
    field4ValuePl: 'Mocne strony: silna baza naziemna GCKS; Słabe strony: brak własnych satelitów na orbicie',
    field4ValueEn: 'Strengths: robust GCKS/NCUVKZ ground assets; Weaknesses: lack of sovereign operational Earth observation orbiters',
    field5LabelUk: 'Методика доказової бази для міжнародних судів',
    field5ValueUk: 'Фіксація збитків за стандартами ООН (UNEP), Ради Європи та підготовка позовів до МКС / ЄСПЛ',
    field5ValuePl: 'Dokumentowanie szkód według standardów UNEP i przygotowanie pozwów do trybunałów międzynarodowych',
    field5ValueEn: 'Damage verification compliant with UN UNEP guidelines for international reparation claims & ICC',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 2: Стан, проблеми та потенціал системи екологічного та космічного контролю України',
    field6ValuePl: 'Rozdział 2: Stan, problemy i potencjał systemu monitoringu kosmiczno-ekologicznego Ukrainy',
    field6ValueEn: 'Chapter 2: State, Challenges, and Potential of Ukraine\'s Space and Environmental Infrastructure',
    notesTextUk: 'Сформувати повний текст Розділу 2 дисертації (Аналіз сучасного стану та проблем України). Провести детальний аудит інфраструктури ДКАУ, НЦУВКЗ, ГЦСК, нормативного поля, оцінити масштаби екологічних втрат від війни за супутниковими та геофізичними даними і сформувати розділ (35–40 стор.) у Word (.docx).',
    notesTextPl: 'Opracować pełny tekst Rozdziału 2 rozprawy doktorskiej (Analiza stanu i problemów Ukrainy). Przeprowadzić audyt infrastruktury DKAU, NCUVKZ, GCKS oraz strat wojennych w formacie Word (.docx).',
    notesTextEn: 'Synthesize the complete Chapter 2 of the PhD dissertation (State of Ukraine, Infrastructure Audit, and War Ecocide Impact). Conduct a thorough review of SSAU/NCUVKZ/GCKS assets and regulatory frameworks in Word (.docx).'
  },
  'phd-national-system-architect': {
    field1LabelUk: 'Цільова концепція створюваної системи',
    field1ValueUk: '«Національна інтегрована система космічно-геофізичної підтримки екологічної безпеки (НІС-ЕКО)»',
    field1ValuePl: '«Narodowy Zintegrowany System Wsparcia Kosmiczno-Geofizycznego Bezpieczeństwa Ekologicznego»',
    field1ValueEn: '«National Integrated Space-Geophysical Environmental Support System of Ukraine (NIS-ECO)»',
    field2LabelUk: 'Сегменти системи (Космічний / Наземний / Хмарний)',
    field2ValueUk: 'Космічний: угруповання 6-8 CubeSat 6U/12U + Наземний: 4 станції ДЗЗ + 24 ГНСС + ГЦСК + Хмарний: AI HPC Hub',
    field2ValuePl: 'Kosmiczny: konstelacja 6-8 CubeSat + Naziemny: 4 stacje EO + 24 GNSS + GCKS + Chmurowy: AI HPC Hub',
    field2ValueEn: 'Space: 6-8 CubeSat 6U constellation + Ground: 4 EO downlinks + 24 GNSS + GCKS + Cloud: AI HPC Situational Hub',
    field3LabelUk: 'Етапи та терміни розгортання (Дорожня карта)',
    field3ValueUk: 'Етап 1 (2026-2027): Інтеграція даних; Етап 2 (2027-2028): Запуск мікросупутників; Етап 3 (2029-2030): Повна автономія',
    field3ValuePl: 'Etap 1 (2026-2027): Integracja danych; Etap 2 (2027-2028): Własne satelity; Etap 3 (2029-2030): Pełna operacyjność',
    field3ValueEn: 'Phase 1 (2026-2027): Data Fusion Layer; Phase 2 (2027-2028): SmallSat Constellation; Phase 3 (2029-2030): Full Autonomy',
    field4LabelUk: 'Інтеграція з європейськими системами (EU Hub)',
    field4ValueUk: 'Copernicus Contributing Mission status, підключення до DestinE Core Service Platform та EU SST Network',
    field4ValuePl: 'Status Copernicus Contributing Mission, integracja z platformą DestinE i siecią EU SST',
    field4ValueEn: 'Copernicus Contributing Mission status, integration with Destination Earth platform & EU SST Network',
    field5LabelUk: 'Оцінка економічного та екологічного ефекту',
    field5ValueUk: 'Зниження ризиків збитків від паводків і пожеж на 35%, прямий економічний ефект: понад 4.2 млрд грн/рік',
    field5ValuePl: 'Redukcja strat powodziowych i pożarowych o 35%, roczny efekt ekonomiczny: 4.2 mld UAH',
    field5ValueEn: '35% mitigation of flood & wildfire damage risks, annual economic benefit exceeding 4.2B UAH',
    field6LabelUk: 'Цільовий розділ дисертації PhD',
    field6ValueUk: 'Розділ 4: Науково-практичні рекомендації та архітектура розгортання Національної системи',
    field6ValuePl: 'Rozdział 4: Rekomendacje naukowo-praktyczne i architektura wdrożenia Systemu Narodowego',
    field6ValueEn: 'Chapter 4: Practical Recommendations and Architectural Blueprint for National Deployment',
    notesTextUk: 'Сформувати повний текст Розділу 4 дисертації (Науково-практичні рекомендації та архітектура системи). Запропонувати триетапну дорожню карту розгортання Національної інтегрованої системи космічно-геофізичної підтримки екології України (НІС-ЕКО), розробити ТЕО, схеми взаємодії відомств та експортувати розділ (35–40 стор.) у Word (.docx).',
    notesTextPl: 'Opracować pełny tekst Rozdziału 4 rozprawy doktorskiej (Rekomendacje i architektura wdrożenia NIS-EKO). Zaprojektować 3-etapową mapę drogową, analizę opłacalności i schematy wdrożeniowe w formacie Word (.docx).',
    notesTextEn: 'Synthesize the complete Chapter 4 of the PhD dissertation (Architectural Blueprint, Feasibility Study, and NIS-ECO Deployment Roadmap). Detail the 3-phase national rollout, capital expenditure plans, and European interoperability in Word (.docx).'
  },
  'phd-academic-compliance-bibliographer': {
    field1LabelUk: 'Загальні висновки дисертації за 5 завданнями',
    field1ValueUk: 'Сформульовано вичерпні підсумки вирішення всіх 5 наукових завдань дослідження',
    field1ValuePl: 'Sformułowano całościowe wnioski odpowiadające na 5 zadań badawczych dysertacji',
    field1ValueEn: 'Comprehensive synthesized conclusions addressing all 5 research objectives of the study',
    field2LabelUk: 'Вимоги до списку використаних джерел',
    field2ValueUk: '200+ наукових джерел за ДСТУ 8302:2015 та APA 7th (Scopus Q1/Q2, WoS, патенти, нормативні акти 2021–2026)',
    field2ValuePl: '200+ źródeł według DSTU 8302:2015 i APA 7th (Scopus Q1/Q2, WoS, patenty, ustawy 2021-2026)',
    field2ValueEn: '200+ peer-reviewed literature sources under DSTU 8302:2015 & APA 7th (Scopus Q1/Q2, WoS, patents 2021-2026)',
    field3LabelUk: 'Структура обов\'язкових Додатків',
    field3ValueUk: 'Додаток А: Акти впровадження (4 установи); Додаток Б: Лістинги коду Python; Додаток В: Проєкт наказу Міндовкілля',
    field3ValuePl: 'Załącznik A: Akty wdrożenia; Załącznik B: Listingi kodu Python; Załącznik C: Projekt rozporządzenia',
    field3ValueEn: 'Appendix A: Implementation Acts (4 agencies); Appendix B: Python Data Fusion Code; Appendix C: Draft Statutory Decrees',
    field4LabelUk: 'Нормоконтроль на відповідність ПКМУ № 44',
    field4ValueUk: 'Перевірка відповідності Порядку присудження ступеня доктора філософії (НАЗЯВО / ДАК)',
    field4ValuePl: 'Weryfikacja zgodności z procedurą nadawania stopnia doktora filozofii (NAQA / MNiSW)',
    field4ValueEn: 'Accreditation compliance audit under Cabinet Decree No. 44 for PhD degree defense',
    field5LabelUk: 'Перевірка на академічний плагіат',
    field5ValueUk: 'Унікальність тексту понад 92% (відповідність вимогам StrikePlagiarism / Unicheck)',
    field5ValuePl: 'Unikalność tekstu powyżej 92% (zgodność z systemami antyplagiatowymi)',
    field5ValueEn: 'Text uniqueness index > 92% conforming to academic anti-plagiarism guidelines',
    field6LabelUk: 'Цільовий підсумковий блок дисертації',
    field6ValueUk: 'Висновки, Список використаних джерел та Додатки А-В',
    field6ValuePl: 'Wnioski końcowe, Bibliografia oraz Załączniki A-C',
    field6ValueEn: 'General Conclusions, Comprehensive Bibliography, and Appendices A-C',
    notesTextUk: 'Сформувати підсумковий блок дисертації доктора філософії: синтезувати 5 пунктів загальних висновків, згенерувати повний бібліографічний список із 200+ профільних джерел (Scopus, WoS, закони) за стандартом ДСТУ 8302:2015 та APA 7th, оформити додатки та перевірити відповідність усім нормам НАЗЯВО у Word (.docx).',
    notesTextPl: 'Wygenerować blok końcowy rozprawy doktorskiej: syntetyczne wnioski, bibliografię 200+ pozycji (Scopus, WoS) w standardzie DSTU 8302:2015 / APA 7th oraz załączniki w formacie Word (.docx).',
    notesTextEn: 'Generate the final block of the doctoral dissertation: synthesize rigorous General Conclusions across all 5 objectives, compile a 200+ source bibliography formatted to DSTU 8302:2015 and APA 7th, assemble Appendices A-C, and export to Word (.docx).'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація академічного ядра обробки дисертаційних матеріалів PhD...",
  "Завантаження та калібрування мультидоменних даних (ДЗЗ, СКАКО, ГНСС, Спецконтроль)...",
  "Синтез математичних моделей комплексування (Data Fusion) та розрахунок матриць ризиків...",
  "Нормоконтроль на відповідність стандартам МОН України, НАЗЯВО та ПКМУ № 44...",
  "Академічне верстання наукового розділу за стандартом ДСТУ 4163:2020 та бібліографії...",
  "Формування підсумкового наукового документа у нативному форматі Word (.docx)..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <GraduationCap className={className} />;
  return <IconComponent className={className} />;
};

export default function PhdAgentView({
  agentId,
  language,
  onBack,
  onGoHome
}: PhdAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Академічний Модуль Дисертації PhD',
    nameEn: 'PhD Dissertation Space-Eco Module',
    descriptionUk: 'Науково-дослідний модуль для написання дисертації доктора філософії.',
    descriptionEn: 'Doctoral research module for PhD dissertation synthesis.',
    icon: 'GraduationCap',
    categoryUk: 'ДОКТОР ФІЛОСОФІЇ & КОСМІЧНА ЕКОЛОГІЯ',
    categoryEn: 'PHD & SPACE-ECO MONITORING'
  };

  const defaultDemo = useMemo(() => {
    return DEFAULT_PHD_DEMOS[agentId] || DEFAULT_PHD_DEMOS['phd-eco-remote-sensing-analyst'];
  }, [agentId]);

  // Clean by default
  const [field1, setField1] = useState('');
  const [field2, setField2] = useState('');
  const [field3, setField3] = useState('');
  const [field4, setField4] = useState('');
  const [field5, setField5] = useState('');
  const [field6, setField6] = useState('');
  const [notesText, setNotesText] = useState('');
  const [isDemoLoaded, setIsDemoLoaded] = useState(false);

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Reset fields on agent change
  useEffect(() => {
    setField1('');
    setField2('');
    setField3('');
    setField4('');
    setField5('');
    setField6('');
    setNotesText('');
    setIsDemoLoaded(false);
    setAttachedFiles([]);
    setReportResult(null);
  }, [agentId]);

  // Sync demo values on language switch if demo was loaded
  useEffect(() => {
    if (isDemoLoaded && defaultDemo) {
      if (language === 'pl') {
        setField1(defaultDemo.field1ValuePl);
        setField2(defaultDemo.field2ValuePl);
        setField3(defaultDemo.field3ValuePl);
        setField4(defaultDemo.field4ValuePl);
        setField5(defaultDemo.field5ValuePl);
        setField6(defaultDemo.field6ValuePl);
        setNotesText(defaultDemo.notesTextPl);
      } else if (language === 'en') {
        setField1(defaultDemo.field1ValueEn);
        setField2(defaultDemo.field2ValueEn);
        setField3(defaultDemo.field3ValueEn);
        setField4(defaultDemo.field4ValueEn);
        setField5(defaultDemo.field5ValueEn);
        setField6(defaultDemo.field6ValueEn);
        setNotesText(defaultDemo.notesTextEn);
      } else {
        setField1(defaultDemo.field1ValueUk);
        setField2(defaultDemo.field2ValueUk);
        setField3(defaultDemo.field3ValueUk);
        setField4(defaultDemo.field4ValueUk);
        setField5(defaultDemo.field5ValueUk);
        setField6(defaultDemo.field6ValueUk);
        setNotesText(defaultDemo.notesTextUk);
      }
    }
  }, [language, isDemoLoaded, defaultDemo]);

  // Auto download Word report once generated
  useEffect(() => {
    if (reportResult && typeof reportResult === 'string' && reportResult.trim().length > 0 && !reportResult.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(reportResult, agentConfig.nameUk || 'PhD_Rozdil_Dysertatsii', agentId);
    }
  }, [reportResult]);

  const handleFillDemo = () => {
    if (!defaultDemo) return;
    if (language === 'pl') {
      setField1(defaultDemo.field1ValuePl);
      setField2(defaultDemo.field2ValuePl);
      setField3(defaultDemo.field3ValuePl);
      setField4(defaultDemo.field4ValuePl);
      setField5(defaultDemo.field5ValuePl);
      setField6(defaultDemo.field6ValuePl);
      setNotesText(defaultDemo.notesTextPl);
    } else if (language === 'en') {
      setField1(defaultDemo.field1ValueEn);
      setField2(defaultDemo.field2ValueEn);
      setField3(defaultDemo.field3ValueEn);
      setField4(defaultDemo.field4ValueEn);
      setField5(defaultDemo.field5ValueEn);
      setField6(defaultDemo.field6ValueEn);
      setNotesText(defaultDemo.notesTextEn);
    } else {
      setField1(defaultDemo.field1ValueUk);
      setField2(defaultDemo.field2ValueUk);
      setField3(defaultDemo.field3ValueUk);
      setField4(defaultDemo.field4ValueUk);
      setField5(defaultDemo.field5ValueUk);
      setField6(defaultDemo.field6ValueUk);
      setNotesText(defaultDemo.notesTextUk);
    }
    setIsDemoLoaded(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          console.warn('Extraction failed for file:', file.name);
        }
      }
    } catch (err) {
      console.error('File reading error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const handleGenerate = async () => {
    setIsProcessing(true);
    setLoadingStepIndex(0);
    setReportResult(null);

    const stepInterval = setInterval(() => {
      setLoadingStepIndex(prev => (prev < LOADING_STEPS_UK.length - 1 ? prev + 1 : prev));
    }, 2500);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const payload = {
        agentId,
        userQuery: notesText || `Скласти науковий підрозділ дисертації PhD на тему «Космічна підтримка екологічного сектору України» за модулем ${agentConfig.nameUk}.`,
        language,
        parameters: {
          [defaultDemo.field1LabelUk]: field1 || defaultDemo.field1ValueUk,
          [defaultDemo.field2LabelUk]: field2 || defaultDemo.field2ValueUk,
          [defaultDemo.field3LabelUk]: field3 || defaultDemo.field3ValueUk,
          [defaultDemo.field4LabelUk]: field4 || defaultDemo.field4ValueUk,
          [defaultDemo.field5LabelUk]: field5 || defaultDemo.field5ValueUk,
          [defaultDemo.field6LabelUk]: field6 || defaultDemo.field6ValueUk,
          "Додаткові наукові матеріали та файли": combinedDocText
        }
      };

      const res = await fetch('/api/generate-agent-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.report || data.content)) {
        setReportResult(data.report || data.content || 'Науковий звіт успішно сформовано!');
      } else {
        const errorText = data.error || data.reason || `Не вдалося отримати відповідь сервера (HTTP ${res.status}).`;
        setReportResult(`⚠️ ПОМИЛКА ГЕНЕРАЦІЇ: ${errorText}`);
      }
    } catch (err: any) {
      setReportResult(`⚠️ ПОМИЛКА: ${err.message || 'Збій з\'єднання'}`);
    } finally {
      clearInterval(stepInterval);
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    if (!reportResult) return;
    navigator.clipboard.writeText(reportResult);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-purple-500/30 rounded-xl p-4 text-white shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-purple-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-purple-500/40"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>
              {isPl ? '← Powrót do Centrum PhD' : isUk ? '← Назад до Центру Дисертацій (PhD)' : '← Back to PhD Center'}
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/40 rounded-md flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5 text-purple-400" />
            🎓 {getLocalizedAgentCategory(agentConfig, language)}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 rounded-md">
            ДСТУ 4163:2020 • НАЗЯВО
          </span>
          <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded flex items-center gap-1">
            <Crown className="w-3 h-3 text-red-400" />
            ADMIN ONLY
          </span>
        </div>
      </div>

      {/* Hero Agent Title & Outcomes */}
      <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900 border-2 border-purple-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-xl overflow-hidden">
        <div className="absolute right-0 top-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-3 max-w-4xl">
          <div className="flex items-center gap-2">
            <div className="p-2.5 bg-purple-600/30 border border-purple-400/50 rounded-xl text-purple-300 shadow-inner">
              <AgentIcon name={agentConfig.icon} className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white">
                {getLocalizedAgentName(agentConfig, language)}
              </h1>
              <div className="text-[11px] font-bold text-purple-300 uppercase tracking-wider">
                Тема: «Космічна підтримка екологічного сектору України» • Дисертація PhD
              </div>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
            {getLocalizedAgentDesc(agentConfig, language)}
          </p>

          <div className="flex items-center gap-3 pt-2 flex-wrap">
            <button
              onClick={handleFillDemo}
              className="px-4 py-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-black text-xs uppercase rounded-xl transition-all shadow-md shadow-amber-600/20 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
            >
              <Sparkles className="w-4 h-4 text-slate-950" />
              <span>{isPl ? '⚡ Wypełnij przykładem demonstracyjnym (1-klik)' : isUk ? '⚡ Заповнити демо-прикладом у 1 клік' : '⚡ Fill 1-Click Demo Preset'}</span>
            </button>
            <span className="text-[11px] text-slate-400 italic">
              {isPl ? 'Wszystkie pola są czyste dla wprowadzenia własnych danych badawczych.' : isUk ? 'Поля форми чисті за замовчуванням для введення ваших наукових даних.' : 'Fields are clean by default for custom research input.'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Interactive Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <h2 className="text-sm font-extrabold uppercase text-purple-200 tracking-wider flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-purple-400" />
            <span>{isPl ? 'Параметри наукового дослідження та вихідні дані' : isUk ? 'Параметри наукового дослідження та вихідні дані' : 'Research Parameters & Input Datasets'}</span>
          </h2>
          <span className="text-[10px] text-slate-400 uppercase font-bold">
            {isPl ? 'Standardy: NAQA • MEiN • DSTU' : isUk ? 'Стандарти: НАЗЯВО • МОН • ДСТУ' : 'Standards: NAQA • MES • DSTU'}
          </span>
        </div>

        {/* 6 Structured Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              1. {defaultDemo.field1LabelUk}
            </label>
            <input
              type="text"
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              placeholder={isPl ? defaultDemo.field1ValuePl : isUk ? defaultDemo.field1ValueUk : defaultDemo.field1ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              2. {defaultDemo.field2LabelUk}
            </label>
            <input
              type="text"
              value={field2}
              onChange={(e) => setField2(e.target.value)}
              placeholder={isPl ? defaultDemo.field2ValuePl : isUk ? defaultDemo.field2ValueUk : defaultDemo.field2ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              3. {defaultDemo.field3LabelUk}
            </label>
            <input
              type="text"
              value={field3}
              onChange={(e) => setField3(e.target.value)}
              placeholder={isPl ? defaultDemo.field3ValuePl : isUk ? defaultDemo.field3ValueUk : defaultDemo.field3ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              4. {defaultDemo.field4LabelUk}
            </label>
            <input
              type="text"
              value={field4}
              onChange={(e) => setField4(e.target.value)}
              placeholder={isPl ? defaultDemo.field4ValuePl : isUk ? defaultDemo.field4ValueUk : defaultDemo.field4ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              5. {defaultDemo.field5LabelUk}
            </label>
            <input
              type="text"
              value={field5}
              onChange={(e) => setField5(e.target.value)}
              placeholder={isPl ? defaultDemo.field5ValuePl : isUk ? defaultDemo.field5ValueUk : defaultDemo.field5ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              6. {defaultDemo.field6LabelUk}
            </label>
            <input
              type="text"
              value={field6}
              onChange={(e) => setField6(e.target.value)}
              placeholder={isPl ? defaultDemo.field6ValuePl : isUk ? defaultDemo.field6ValueUk : defaultDemo.field6ValueEn}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
            />
          </div>
        </div>

        {/* Detailed Scientific Prompt / Notes Area */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
            <span>7. Наукове завдання, формулювання новизни та вимоги до тексту:</span>
            <span className="text-[10px] text-purple-300">Форматування за ДСТУ 4163:2020</span>
          </label>
          <textarea
            rows={4}
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder={isPl ? defaultDemo.notesTextPl : isUk ? defaultDemo.notesTextUk : defaultDemo.notesTextEn}
            className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl p-4 text-xs text-slate-100 placeholder-slate-600 transition-colors font-mono"
          />
        </div>

        {/* File Attachments Area */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs font-bold text-slate-300 uppercase">
              Прикріпити первинні матеріали (PDF, DOCX, CSV, TXT, XML):
            </span>
            <label className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-purple-300 text-xs font-bold uppercase rounded-xl transition-all border border-purple-500/30 cursor-pointer flex items-center gap-2">
              <Upload className="w-3.5 h-3.5" />
              <span>{isReadingFile ? 'Читання файлів...' : 'Обрати файли дослідження'}</span>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.txt,.csv,.xml,.json,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isReadingFile || isProcessing}
              />
            </label>
          </div>

          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {attachedFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-1.5 bg-slate-950 border border-purple-500/40 px-3 py-1 rounded-lg text-xs text-purple-200">
                  <FileText className="w-3.5 h-3.5 text-purple-400" />
                  <span className="truncate max-w-[200px]">{file.name}</span>
                  <button
                    onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))}
                    className="text-slate-500 hover:text-red-400 ml-1 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-800 flex items-center justify-between flex-wrap gap-4">
          <div className="text-xs text-slate-400">
            <span className="font-bold text-purple-300">ШІ-Ядро:</span> Gemini 3.7 Flash High-Precision Multimodal + ДСТУ Linter
          </div>

          <button
            onClick={handleGenerate}
            disabled={isProcessing || isReadingFile}
            className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-xl shadow-purple-600/30 flex items-center gap-3 cursor-pointer hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-purple-200" />
                <span>Генерація наукового звіту...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-purple-200" />
                <span>🚀 Сформувати Розділ Дисертації у Word (.docx)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading Progress State */}
      {isProcessing && (
        <div className="bg-slate-900 border border-purple-500/40 rounded-2xl p-6 text-white shadow-xl space-y-4 font-mono animate-pulse">
          <div className="flex items-center justify-between text-xs font-bold text-purple-300">
            <span>Крок {loadingStepIndex + 1} із {LOADING_STEPS_UK.length}:</span>
            <span>{Math.round(((loadingStepIndex + 1) / LOADING_STEPS_UK.length) * 100)}%</span>
          </div>
          <div className="w-full bg-slate-950 h-2 rounded-full overflow-hidden border border-slate-800">
            <div
              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full transition-all duration-500"
              style={{ width: `${((loadingStepIndex + 1) / LOADING_STEPS_UK.length) * 100}%` }}
            />
          </div>
          <p className="text-xs text-slate-300 text-center font-semibold">
            {LOADING_STEPS_UK[loadingStepIndex]}
          </p>
        </div>
      )}

      {/* Generated Report Result Canvas */}
      {reportResult && (
        <div className="bg-slate-900 border border-purple-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-400 shadow-xs shadow-emerald-400 animate-pulse" />
              <h3 className="text-base font-extrabold uppercase text-emerald-300 tracking-wider">
                Офіційний Науковий Звіт Сформовано (ДСТУ 4163:2020)
              </h3>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{isCopied ? 'Скопійовано!' : 'Копіювати'}</span>
              </button>

              <button
                onClick={() => autoDownloadReport(reportResult, agentConfig.nameUk || 'PhD_Rozdil', agentId)}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-black uppercase rounded-lg transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Завантажити Word (.docx)</span>
              </button>

              <TelegramExportButton
                reportText={reportResult}
                agentName={agentConfig.nameUk}
                language={language}
              />
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 font-sans text-xs text-slate-200 leading-relaxed overflow-x-auto max-h-[700px] overflow-y-auto whitespace-pre-wrap selection:bg-purple-500 selection:text-white">
            {reportResult}
          </div>
        </div>
      )}
    </div>
  );
}
